import { EN } from './i18n/en';
import { siteTermOf, siteText, type SiteTerm } from './siteTerms';

/**
 * Limba aplicației (2026-10-28): română și engleză.
 *
 * Textele rămân scrise în cod în română: textul românesc e cheia, iar `i18n/en.ts`
 * are fraza în engleză, scrisă ca un om (nu cuvânt cu cuvânt). Ce lipsește din
 * dicționar iese în română; testul `i18n.test.ts` caută toate `t('…')` din cod și
 * cere traducerea fiecăruia.
 *
 * Valorile se pun cu {0}, {1}…: `t('Ai {0} cereri deschise', n)`. Pluralul se scrie
 * ca până acum, cu câte un text pe ramură (`n === 1 ? t('un furnizor') : t('{0} furnizori', n)`).
 *
 * Limba și cuvântul firmei pentru șantier vin dintr-o sursă: în browser, omul din
 * sesiune; pe server, cererea în lucru sau mailul care se scrie (`AsyncLocalStorage`,
 * vezi apps/api/src/lang.ts). Așa, funcțiile din shared care scriu texte (alerte,
 * mailuri, mesaje) nu primesc limba ca parametru.
 */

export type Lang = 'ro' | 'en';

export const LANGS: readonly { id: Lang; label: string }[] = [
  { id: 'ro', label: 'Română' },
  { id: 'en', label: 'English' },
];

export const isLang = (v: unknown): v is Lang => v === 'ro' || v === 'en';

/** Orice valoare necunoscută rămâne pe română, limba de până acum. */
export const langOf = (v: unknown): Lang => (isLang(v) ? v : 'ro');

export interface LangContext {
  lang: Lang;
  /** cuvântul firmei pentru șantier (`Company.siteTerm`) */
  term: string | null;
  /** moneda firmei (`Company.currency`); lipsă = RON */
  currency?: string | null;
  /** țara firmei (`Company.country`); lipsă = RO */
  country?: string | null;
}

let source: () => LangContext = () => ({ lang: 'ro', term: null });

/** Cine spune limba acum: browserul (omul din sesiune) sau serverul (cererea în lucru). */
export function setLangSource(f: () => LangContext): void {
  source = f;
}

export const curLang = (): Lang => source().lang;

export const curTerm = (): SiteTerm => siteTermOf(source().term);

/**
 * Moneda firmei. În bază, sumele „în lei” (`Currency` 'RON') sunt de fapt în moneda
 * firmei: o firmă din Germania lucrează în euro, una din Anglia în lire. Ofertele
 * într-o altă monedă (euro, cu cursul BNR) există doar la firmele care lucrează în lei.
 */
export const baseCurrency = (): string => source().currency || 'RON';

export const baseCountry = (): string => source().country || 'RO';

/** Firma lucrează în lei (România, Moldova): textele cu „lei” rămân cum sunt. */
export const usesLei = (): boolean => ['RON', 'MDL'].includes(baseCurrency());

/** Semnul monedei firmei: „lei”, „€”, „£”, „$”… */
export function baseSign(): string {
  if (usesLei()) return translate('lei');
  try {
    return (
      new Intl.NumberFormat(intlLocale(), {
        style: 'currency',
        currency: baseCurrency(),
        currencyDisplay: 'narrowSymbol',
      })
        .formatToParts(0)
        .find((p) => p.type === 'currency')?.value ?? baseCurrency()
    );
  } catch {
    return baseCurrency();
  }
}

/** Pentru `Intl`: engleza britanică (zi, lună, an; piața e Europa). */
export const intlLocale = (lang: Lang = curLang()): string => (lang === 'en' ? 'en-GB' : 'ro-RO');

/** O valoare pusă în frază; lipsă (null, undefined) = nimic. */
export type TArg = string | number | null | undefined;

const fill = (s: string, args: readonly TArg[]): string =>
  args.length
    ? s.replace(/\{(\d+)\}/g, (m, i: string) => (+i < args.length ? String(args[+i] ?? '') : m))
    : s;

/** Fraza în limba dată, fără cuvântul firmei și fără valori. */
export const translate = (ro: string, lang: Lang = curLang()): string =>
  lang === 'en' ? (EN[ro] ?? ro) : ro;

/** Are fraza traducere în engleză? (pentru teste și verificări) */
export const hasEn = (ro: string): boolean => Object.prototype.hasOwnProperty.call(EN, ro);

/**
 * Textul în limba de acum, cu cuvântul firmei pentru șantier, apoi cu valorile
 * puse în locul lui {0}, {1}… (valorile, de obicei nume scrise de oameni, nu se
 * schimbă: „Șantier Nord” rămâne „Șantier Nord”).
 */
export function t(ro: string, ...args: TArg[]): string {
  const { lang, term } = source();
  return fill(inCurrency(siteText(translate(ro, lang), siteTermOf(term), lang)), args);
}

/** Textele spun „lei”; la o firmă care lucrează în altă monedă, apare semnul ei. */
function inCurrency(s: string): string {
  if (usesLei()) return s;
  const sign = baseSign();
  // în engleză semnul stă înaintea sumei: „{0} lei” → „£{0}”
  if (curLang() === 'en' && sign.length <= 2)
    s = s.replace(/(\{\d+\})\s+lei(?![\p{L}])/gu, (_m, n: string) => `${sign}${n}`);
  return s.replace(/(?<![\p{L}])lei(?![\p{L}])/gu, sign);
}

let patterns: { re: RegExp; en: string }[] | null = null;

/** Frazele din dicționar cu {0}, {1}…, ca tipare: „Furnizorul „(.+)” nu există.” */
function compiled(): { re: RegExp; en: string }[] {
  if (!patterns) {
    patterns = Object.entries(EN)
      .filter(([ro]) => /\{\d+\}/.test(ro))
      .map(([ro, en]) => {
        const order: number[] = [];
        const src = ro
          .split(/(\{\d+\})/)
          .map((part) => {
            const m = /^\{(\d+)\}$/.exec(part);
            if (!m) return part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            order.push(+m[1]!);
            return '([\\s\\S]+?)';
          })
          .join('');
        // valorile prinse se pun la locul lor în engleză, după numărul din acolade
        const en2 = en.replace(/\{(\d+)\}/g, (_m, i: string) => `{${order.indexOf(+i)}}`);
        return { re: new RegExp(`^${src}$`), en: en2 };
      });
  }
  return patterns;
}

/**
 * Un text deja scris în română, cu valorile puse (un mesaj de eroare de pe server,
 * un text calculat în shared), adus în limba de acum: întâi exact, apoi după tiparele
 * cu {0}, {1}… din dicționar. Ce nu se recunoaște rămâne cum e.
 */
export function translateLoose(ro: string): string {
  const { lang, term } = source();
  if (lang !== 'en' || hasEn(ro)) return t(ro);
  for (const p of compiled()) {
    const m = p.re.exec(ro);
    if (m) return fill(inCurrency(siteText(p.en, siteTermOf(term), lang)), m.slice(1));
  }
  return inCurrency(siteText(ro, siteTermOf(term), lang));
}
